import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { Line } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler } from 'chart.js';
import ContractPreview from '../components/ContractPreview';
import LoanDetails from '../components/Forms/LoanDetails';
import SharedLoanEditForm from '../components/Forms/LoanEditForm';
import ClientInformationConsentAgreement from '../components/ClientInformationConsentAgreement';
import DebitOrderConsentAgreement from '../components/DebitOrderConsentAgreement';
import AnnexureA from '../components/AnnexureA';
import Button from '../components/UI/Button';
import ActionButton from '../components/UI/ActionButton';
import { formatDateDMY } from '../utils/dateUtils';
import InvoiceModal from '../components/InvoiceModal';
import ResponsiveTable from '../components/ResponsiveTable';
import { enrichLoanForInvoice } from '../utils/invoiceUtils';
import StaffSavedApplications from '../components/StaffSavedApplications';
import FlaggedDocumentsCard from '../components/FlaggedDocumentsCard';
import UndoRepaymentConfirm from '../components/UndoRepaymentConfirm';
import ClientLink from '../components/ClientLink';
import StatusBadge from '../components/UI/StatusBadge';
import LoanLink from '../components/LoanLink';
import LicenceAlert from '../components/LicenceAlert';
import DebitOrderBatchModal from '../components/DebitOrderBatchModal';
import WriteOffCandidatesPanel from '../components/WriteOffCandidatesPanel';
import { AuthContext } from '../context/AuthContext';
import { toCurrency } from '../utils/adminMetrics';
import Pager from '../components/UI/Pager';
import KpiCard from '../components/Admin/KpiCard';
import ChartCard from '../components/Admin/ChartCard';

// Heroicons SVGs for section icons.
const UserIcon = () => (
  <svg className="w-6 h-6 text-blue-600 mr-2 inline-block" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <circle cx="12" cy="8" r="4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 20v-2a6 6 0 0112 0v2" />
  </svg>
);

const CashIcon = () => (
  <svg className="w-6 h-6 text-yellow-600 mr-2 inline-block" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <rect width="20" height="12" x="2" y="6" rx="2" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const RepaymentIcon = () => (
  <svg className="w-6 h-6 text-green-600 mr-2 inline-block" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" />
    <circle cx="12" cy="12" r="10" />
  </svg>
);

// Repayment Form Modal (Admin Dashboard)
const RepaymentForm = ({ loan, clientId, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [refTouched, setRefTouched] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!loan?.id) return;
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.post(
        apiUrl(`/api/loans/${loan.id}/repayments`),
        {
          client_id: clientId,
          amount_paid: amount,
          payment_date: date,
          payment_method: paymentMethod,
          payment_reference: paymentReference.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: 'Repayment entered successfully!' });
      setAmount('');
      setDate('');
      setPaymentMethod('');
      setPaymentReference('');
      setRefTouched(false);
      if (onSuccess) await onSuccess();
    } catch (err) {
      const apiError = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Error entering repayment.';
      setMessage({ type: 'error', text: apiError });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden relative dark:bg-slate-800">
        <div className="h-1" style={{ background: 'linear-gradient(90deg, #16a34a, #15803d)' }} />
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between dark:border-slate-700 dark:bg-slate-700/60">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
            <RepaymentIcon /> Enter Repayment
          </h3>
          <Button
            onClick={onClose}
            variant="text"
            size="sm"
            className="!p-1.5 rounded-full"
            title="Close"
            disabled={submitting}
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            }
          />
        </div>
        <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">Amount</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              className="w-full"
              min="0.01"
              step="0.01"
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="w-full"
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">Payment Method</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              required
              className="w-full"
              disabled={submitting}
            >
              <option value="">Select method</option>
              <option value="Cash">Cash</option>
              <option value="EFT/Bank Transfer">EFT/Bank Transfer</option>
              <option value="Debit Order">Debit Order</option>
              <option value="Card (POS/Speedpoint)">Card (POS/Speedpoint)</option>
              <option value="PayToday">PayToday</option>
              <option value="PayPulse">PayPulse</option>
              <option value="PayPulse (Bank Windhoek)">PayPulse (Bank Windhoek)</option>
              <option value="BlueWallet">BlueWallet</option>
              <option value="FNB eWallet/Pay2Cell">FNB eWallet/Pay2Cell</option>
              <option value="ATM Cash Deposit">ATM Cash Deposit</option>
              <option value="Stop Order">Stop Order</option>
              <option value="MTC Pay">MTC Pay</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">Reference <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              onBlur={() => setRefTouched(true)}
              placeholder="e.g., EFT ref, slip no., Txn ID"
              required
              className="w-full"
              disabled={submitting}
            />
          <p className={`mt-1 text-xs ${refTouched && !paymentReference.trim() ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-slate-400'}`}>
            {refTouched && !paymentReference.trim() ? 'Enter the payment reference.' : 'Required: the EFT reference, slip number or transaction ID.'}
          </p>
          </div>
          <div className="pt-2 flex justify-end gap-3 border-t border-indigo-50 dark:border-slate-700 mt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm border border-gray-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600"
              disabled={submitting}
            >
              Cancel
            </button>
            <ActionButton
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500"
              disabled={submitting}
              busyLabel="Submitting…"
              onClick={(e) => {
                const formEl = e.currentTarget.form;
                if (formEl && !formEl.reportValidity()) { e.preventDefault(); return; }
                return handleSubmit(e);
              }}
            >
              {submitting ? 'Submitting…' : 'Submit Repayment'}
            </ActionButton>
          </div>
          {message.text && (
            <div className={`text-sm text-center py-2 px-3 rounded-lg ${message.type === 'error' ? 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-700/50' : 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-100 dark:border-green-700/50'}`}>
              {message.text}
            </div>
          )}
        </form>
        </div>
      </div>
    </div>
  );
};

const ConfirmActionModal = ({
  open,
  title,
  message,
  showNote,
  noteRequired,
  noteLabel,
  notePlaceholder,
  confirmText,
  confirmClassName,
  onClose,
  onConfirm,
  initialNote = '',
}) => {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const trimmedNote = (note || '').trim();

  useEffect(() => {
    if (open) {
      setNote(initialNote || '');
      setSubmitting(false);
      setError('');
    }
  }, [open, initialNote]);

  if (!open) return null;

  const handleConfirm = async () => {
    try {
      if (showNote && noteRequired && !trimmedNote) {
        setError('Note / reason is required.');
        return;
      }
      setSubmitting(true);
      setError('');
      await onConfirm(trimmedNote);
    } catch (e) {
      const msg = e?.message || 'Action failed.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-xl overflow-hidden w-full max-w-md dark:bg-slate-800"
        style={{ boxShadow: '0 20px 40px -8px rgba(0,0,0,0.18), 0 4px 16px -4px rgba(79,70,229,0.12)' }}
      >
        <div className="h-1" style={{ background: 'linear-gradient(90deg, #3b82f6, #6366f1, #8b5cf6)' }} />
        <div className="px-5 py-3.5 border-b border-indigo-100/60 dark:border-indigo-900/60 flex items-center justify-between bg-gradient-to-r from-gray-50 to-indigo-50/20 dark:from-slate-800 dark:to-slate-800/80">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
            <span className="w-1 h-4 rounded-full bg-gradient-to-b from-blue-500 to-indigo-500 inline-block flex-shrink-0" />
            {title}
          </h3>
          <Button
            onClick={onClose}
            variant="text"
            size="sm"
            className="!p-1.5 rounded-full hover:bg-indigo-100/60 dark:hover:bg-slate-700"
            title="Close"
            disabled={submitting}
            icon={
              <svg className="w-4 h-4 text-gray-400 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            }
          />
        </div>
        <div className="p-5">
        <p className="text-sm text-gray-700 dark:text-slate-300 mb-4">{message}</p>

        {showNote && (
          <div className="mb-4">
            <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">{noteLabel || 'Note / reason'}</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full h-24 resize-none"
              placeholder={notePlaceholder || 'Optional note…'}
              disabled={submitting}
            />
          </div>
        )}

        {error && (
          <div className="mb-4 p-2.5 rounded-lg border border-red-200 dark:border-red-700/50 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">{error}</div>
        )}

        <div className="flex justify-end gap-3 pt-2 border-t border-indigo-50 dark:border-slate-700 mt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm border border-gray-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600"
          >
            Cancel
          </button>
          <ActionButton
            type="button"
            onClick={handleConfirm}
            disabled={submitting || (showNote && noteRequired && !trimmedNote)}
            className={`px-4 py-2 rounded-lg transition-colors font-medium text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 ${confirmClassName || 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'}`}
            busyLabel="Please wait…"
          >
            {submitting ? 'Please wait…' : (confirmText || 'Confirm')}
          </ActionButton>
        </div>
        </div>
      </div>
    </div>
  );
};

// The contract for staff to sign as lender and witness on a loan Awaiting
// Staff, with what the client filled in. It is shown inside the shared Loan
// Details screen (staffSigning), so every loan opens in the same screen.
const StaffSigningContract = ({ item, clients, onGenerate, isLoading, onEdit }) => {
  const parsedRefs = (() => {
    try {
      if (!item?.references_json) return { reference1_name: '', reference1_tel: '', reference2_name: '', reference2_tel: '' };
      return typeof item.references_json === 'string' ? JSON.parse(item.references_json) : item.references_json;
    } catch {
      return { reference1_name: '', reference1_tel: '', reference2_name: '', reference2_tel: '' };
    }
  })();
  const parsedLocs = (() => {
    try {
      if (!item?.signature_locations) return { borrowerLocation: 'Windhoek', lenderLocation: 'Windhoek' };
      return typeof item.signature_locations === 'string' ? JSON.parse(item.signature_locations) : item.signature_locations;
    } catch {
      return { borrowerLocation: 'Windhoek', lenderLocation: 'Windhoek' };
    }
  })();

  // Full client details from the loaded clients (loans.client_id = personal_info.id).
  const clientRecord = Array.isArray(clients)
    ? clients.find(c => c?.personal_info?.id === item?.client_id)
    : null;
  const pi = clientRecord?.personal_info || {};
  const oi = clientRecord?.occupation_info || {};
  const bi = clientRecord?.bank_info || {};
  const clientData = {
    id: pi.id || item?.client_id || item?.clientId || '',
    first_name: pi.first_name || '',
    middle_name: pi.middle_name || '',
    last_name: pi.last_name || '',
    id_passport_no: pi.id_passport_no || '',
    residential_address: pi.residential_address || '',
    phone_number: pi.phone_number || '',
    email: pi.email || '',
    postal_address: pi.postal_address || '',
    marital_status: pi.marital_status || '',
    occupation: oi.occupation || '',
    employer_phone: oi.employer_phone || '',
    employer_name: oi.employer_name || '',
    employer_address: oi.employer_address || '',
    payslip_number: oi.payslip_number || '',
    bank_name: bi.bank_name || '',
    branch_code: bi.branch_code || '',
    account_number: bi.account_number || '',
    account_type: bi.account_type || '',
    ...parsedRefs,
  };

  const initialValues = {
    signature: item.borrower_signature || null,
    lenderSignature: item.lender_signature || null,
    witness2Signature: item.witness2_signature || null,
    initials: {
      borrowerInitials: item.borrower_initials || null,
      lenderInitials: item.lender_initials || null,
      witness2Initials: item.witness2_initials || null,
    },
    signatureLocations: parsedLocs,
  };

  return (
    <ContractPreview
      loanData={item}
      clientData={clientData}
      onEdit={() => { if (typeof onEdit === 'function') onEdit(); }}
      onGenerate={(updatedClientData) => onGenerate(updatedClientData, item)}
      loading={isLoading}
      initialValues={initialValues}
      // Shown inside Loan Details, which lists the documents already.
      hideDocuments
    />
  );
};

const LoanEditForm = ({ loan, onClose, onSuccess }) => {
  const [principal, setPrincipal] = useState(loan.principal_amount || '');
  const [interest, setInterest] = useState(loan.interest_rate || '');
  const [duration, setDuration] = useState(loan.duration_months || '');
  const [dueDate, setDueDate] = useState(loan.due_date ? loan.due_date.slice(0, 10) : '');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [interestRates, setInterestRates] = useState([]);

  useEffect(() => {
    // Fetch interest rates from API
    const fetchRates = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl('/api/interestrates'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setInterestRates(res.data.filter(r => r.is_active));
      } catch (err) {
        setInterestRates([]);
      }
    };
    fetchRates();
  }, [loan.interest_rate]);

  // Ensure current interest is always in the dropdown
  const allRates = interestRates.some(r => String(r.rate) === String(loan.interest_rate))
    ? interestRates
    : loan.interest_rate
    ? [
        ...interestRates,
        {
          id: 'current',
          rate: loan.interest_rate,
          description: 'Current Rate (inactive)',
          is_active: false,
        }
      ]
    : interestRates;

  const handleSubmit = async e => {
    e.preventDefault();
    setMessage('');
    setMessageType('info');
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        apiUrl(`/api/loans/${loan.id}`),
        {
          principal_amount: principal,
          interest_rate: interest,
          duration_months: duration,
          due_date: dueDate,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage('Loan updated successfully!');
      setMessageType('success');
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 1200); // Show message for 1.2 seconds before closing
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        setMessage(`Update failed: ${err.response.data.message}`);
      } else if (err.response && err.response.status === 400) {
        setMessage('Update failed: Invalid loan details.');
      } else if (err.response && err.response.status === 403) {
        setMessage('Update failed: You do not have permission.');
      } else {
        setMessage('Error updating loan.');
      }
      setMessageType('error');
    }
  };

  const getEditMessageClass = () => {
    switch (messageType) {
      case 'success':
        return 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700/50';
      case 'error':
        return 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700/50';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700/50';
      default:
        return 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700/50';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-xl overflow-hidden w-full max-w-lg dark:bg-slate-800"
        style={{ boxShadow: '0 20px 40px -8px rgba(0,0,0,0.18), 0 4px 16px -4px rgba(79,70,229,0.12)' }}
      >
        <div className="h-1" style={{ background: 'linear-gradient(90deg, #3b82f6, #6366f1, #8b5cf6)' }} />
        <div className="px-5 py-3.5 border-b border-indigo-100/60 dark:border-indigo-900/60 flex items-center justify-between bg-gradient-to-r from-gray-50 to-indigo-50/20 dark:from-slate-800 dark:to-slate-800/80">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
            <span className="w-1 h-4 rounded-full bg-gradient-to-b from-blue-500 to-indigo-500 inline-block flex-shrink-0" />
            <CashIcon />
            Edit Loan Details
          </h3>
          <Button
            onClick={onClose}
            variant="text"
            size="sm"
            className="!p-1.5 rounded-full hover:bg-indigo-100/60 dark:hover:bg-slate-700"
            title="Close"
            icon={
              <svg className="w-4 h-4 text-gray-400 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            }
          />
        </div>
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">Principal Amount</label>
              <input
                type="number"
                value={principal}
                onChange={e => setPrincipal(e.target.value)}
                required
                className="w-full"
                min="1"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">Interest Rate (%)</label>
              <select
                value={interest}
                onChange={e => setInterest(e.target.value)}
                required
                className="w-full"
              >
                <option value="">Select interest rate</option>
                {allRates.map(rate => (
                  <option key={rate.id} value={rate.rate}>
                    {rate.rate}%
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">Duration (months)</label>
              <input
                type="number"
                value={duration}
                onChange={e => setDuration(e.target.value)}
                required
                className="w-full"
                min="1"
                max="5"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                required
                className="w-full"
              />
            </div>
            <div className="pt-2 flex justify-end gap-3 border-t border-indigo-50 dark:border-slate-700">
              <Button
                type="submit"
                variant="primary"
                size="md"
                busyLabel="Saving…"
                onClick={(e) => {
                  const formEl = e.currentTarget.form;
                  if (formEl && !formEl.reportValidity()) { e.preventDefault(); return; }
                  return handleSubmit(e);
                }}
              >
                Save Changes
              </Button>
            </div>
            {message && (
              <div className={`p-3 rounded-lg border text-sm ${getEditMessageClass()}`}>
                {message}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const PAGE_SIZE = 10;
const OVERDUE_PAGE_SIZE = 5;

// Dashboard work queues sit side by side, each in its own panel with a
// coloured header (title, count, paging) so they read as separate lists.
const QUEUE_TONES = {
  yellow: { bar: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800/50', dot: 'bg-yellow-500', ring: 'border-t-yellow-500', badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' },
  indigo: { bar: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800/50', dot: 'bg-indigo-500', ring: 'border-t-indigo-500', badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300' },
  red: { bar: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50', dot: 'bg-red-500', ring: 'border-t-red-500', badge: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' },
  rose: { bar: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800/50', dot: 'bg-rose-500', ring: 'border-t-rose-500', badge: 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-300' },
  emerald: { bar: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50', dot: 'bg-emerald-500', ring: 'border-t-emerald-500', badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300' },
};

// Each queue shows 3 loans at a time, and can be searched (loan code or
// client) and limited to a date range on its own date field.
const QUEUE_PAGE_SIZE = 3;
const dayKey = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
function useQueueView(items, dateOf, textOf) {
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const list = Array.isArray(items) ? items : [];
  const term = q.trim().toLowerCase();
  const filtered = list.filter((it) => {
    const day = dayKey(dateOf(it));
    if (from && (!day || day < from)) return false;
    if (to && (!day || day > to)) return false;
    return !term || String(textOf(it) || '').toLowerCase().includes(term);
  });
  const pages = Math.max(1, Math.ceil(filtered.length / QUEUE_PAGE_SIZE));
  const current = Math.min(page, pages);
  const rows = filtered.slice((current - 1) * QUEUE_PAGE_SIZE, current * QUEUE_PAGE_SIZE);
  const set = (fn) => (v) => { fn(v); setPage(1); };
  return {
    q, setQ: set(setQ), from, setFrom: set(setFrom), to, setTo: set(setTo),
    page: current, setPage, pages, rows, total: filtered.length, filtering: Boolean(term || from || to),
    clear: () => { setQ(''); setFrom(''); setTo(''); setPage(1); },
  };
}
// One line, however narrow the panel: the search box gives up its width,
// the label, the two dates and Clear keep theirs.
const QueueToolbar = ({ view, dateLabel, placeholder = 'Search loan code or client' }) => (
  <div className="flex flex-nowrap items-center gap-1 px-2 pt-2 text-[11px] text-gray-500 dark:text-slate-400">
    <input
      type="text"
      value={view.q}
      onChange={(e) => view.setQ(e.target.value)}
      placeholder={placeholder}
      title={placeholder}
      aria-label="Search this queue"
      className="flex-1 min-w-0 !h-7"
    />
    <span title={dateLabel} className="shrink-0 whitespace-nowrap">{dateLabel}</span>
    <input type="date" value={view.from} max={view.to || undefined} onChange={(e) => view.setFrom(e.target.value)} aria-label={`${dateLabel} from`} className="!h-7 w-[112px] shrink-0 !px-1.5" />
    <span className="shrink-0">–</span>
    <input type="date" value={view.to} min={view.from || undefined} onChange={(e) => view.setTo(e.target.value)} aria-label={`${dateLabel} to`} className="!h-7 w-[112px] shrink-0 !px-1.5" />
    {view.filtering && (
      <button type="button" onClick={view.clear} className="shrink-0 text-blue-600 dark:text-blue-300 hover:underline">Clear</button>
    )}
  </div>
);

const QueuePanel = ({ title, tone = 'yellow', count, right = null, toolbar = null, children }) => {
  const t = QUEUE_TONES[tone] || QUEUE_TONES.yellow;
  return (
    <section className={`flex flex-col rounded-xl border border-gray-200 dark:border-slate-700 border-t-4 ${t.ring} bg-white dark:bg-slate-800 shadow-sm overflow-hidden`}>
      <header className={`flex items-center justify-between gap-2 px-3 py-2 border-b ${t.bar}`}>
        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-100">
          <span className={`inline-block w-2 h-2 rounded-sm ${t.dot}`} />
          {title}
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${t.badge}`}>{count}</span>
        </h2>
        {right}
      </header>
      {toolbar}
      <div className="flex-1 pt-2 space-y-2">{children}</div>
    </section>
  );
};

const QueueEmpty = ({ children }) => (
  <p className="py-6 text-center text-xs text-gray-500 dark:text-slate-400">{children}</p>
);

// The loan queues' first column: the loan code (opens the loan) with the
// client under it, so a queue fits a third of the screen. tag: mark a loan the
// calculator declined (not in the declined queue, where every loan is).
const queueLoanCol = (open, tag = true) => ({
  field: 'loan_code',
  header: 'Loan',
  formatter: (v, item) => (
    <div className="min-w-0">
      <div className="whitespace-nowrap"><LoanLink loan={item} onOpen={() => open(item)}>{v || item.loanCode || '-'}</LoanLink></div>
      <div className="text-[11px]"><ClientLink clientId={item.client_id ?? item.clientId} name={item.clientName || '-'} /></div>
      {tag && item.calculator?.declined && <StatusBadge status="Rejected" label="Calculator declined" size="small" className="mt-0.5" />}
    </div>
  ),
});

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user: authUser } = React.useContext(AuthContext);
  const authPerms = Array.isArray(authUser?.permissions) ? authUser.permissions : [];
  const canOpenLoansPage = authPerms.includes('*') || authPerms.includes('ViewLoans') || authPerms.includes('ManageLoans');
  const canOpenClientsPage = authPerms.includes('*') || authPerms.includes('ManageClients');
  const canOpenRepaymentsPage = canOpenLoansPage || authPerms.includes('EnterRepayment');
  // The debit order file: the users who approve or finalise loans (the API checks the same).
  const canCollectDebitOrders = ['*', 'ApproveLoans', 'EditLoan', 'ManageLoans'].some((p) => authPerms.includes(p));
  const [showDebitOrders, setShowDebitOrders] = useState(false);
  // A local calendar day as YYYY-MM-DD, for /repayments?from=&to= links.
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  // A stat tile opens the page that lists what it counts, already filtered.
  const tileGo = (allowed, path) => (allowed ? () => navigate(path) : null);
  const [clients, setClients] = useState([]);
  const [clientLimitStatus, setClientLimitStatus] = useState(null);
  const [loans, setLoans] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // Page of the Awaiting Staff Signature queue panel.
  const [queuePage, setQueuePage] = useState(1);
  const [showActions, setShowActions] = useState({});
  const [overduePage, setOverduePage] = useState(1);
  const [confirmCtx, setConfirmCtx] = useState(null); // { kind, loan, extra }
  // The undo-repayment form (the one used everywhere).
  const [undoCtx, setUndoCtx] = useState(null);
  const [writeOffRefresh, setWriteOffRefresh] = useState(0);
  const [actionMessage, setActionMessage] = useState('');
  const [actionType, setActionType] = useState('info');
  const [showLoanDetails, setShowLoanDetails] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loanDetailsSource, setLoanDetailsSource] = useState(''); // '', 'overdue', 'awaiting-staff', 'other'
  const [loanDocuments, setLoanDocuments] = useState([]);
  const [showRepaymentForm, setShowRepaymentForm] = useState(false);
  const [repaymentLoan, setRepaymentLoan] = useState(null);
  const [repaymentClientId, setRepaymentClientId] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceLoan, setInvoiceLoan] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editLoan, setEditLoan] = useState(null);
  const [editMode, setEditMode] = useState('');
  const [awaitingQueue, setAwaitingQueue] = useState([]);
  const [queueError, setQueueError] = useState('');
  const [showQueue, setShowQueue] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '' });
  const [totalItems, setTotalItems] = useState(0);
  // For LoanDetails resend application form note flow
  const [moreDetailsNotes, setMoreDetailsNotes] = useState({});
  const [showNoteInput, setShowNoteInput] = useState({});
  // Admin-only enhancements
  const [reports, setReports] = useState({ totalLoans: 0, overdueInvoices: 0 });
  const [apiHealthy, setApiHealthy] = useState(false);
  const [overdueTriggering, setOverdueTriggering] = useState(false);
  // Bulk repayments upload
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);


  const downloadSampleCsv = () => {
    const headers = ['loan_id','client_id','amount_paid','payment_date','payment_method','payment_reference'];
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2,'0');
    const dd = String(today.getDate()).padStart(2,'0');
    const dateStr = `${yyyy}-${mm}-${dd}`;
    const rows = [
      ['101','12','500.00',dateStr,'EFT/Bank Transfer','INV-1001'],
      ['102','15','750.00',dateStr,'Card (POS/Speedpoint)','SLIP-5678'],
      ['103','18','1000.00',dateStr,'PayToday','PT-REF-ABCD']
    ];
    const escape = (v) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? '"' + s.replace(/"/g,'""') + '"' : s;
    };
    const csv = [headers.join(','), ...rows.map(r => r.map(escape).join(','))].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk-payments-sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const [rejectDialog, setRejectDialog] = useState({ open: false, loanId: null, reason: '' });
  const [monthsWindow, setMonthsWindow] = useState(12);

  // Clear alert after 5 seconds
  useEffect(() => {
    if (alert.message) {
      const timer = setTimeout(() => {
        setAlert({ type: '', message: '' });
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [alert]);
  
  // No auto-close timer needed

  const actionsPopupRef = useRef(null);

  // What staff may do with each loan comes from the API (loan.state.staff_actions:
  // status rules and this user's permissions applied). No state, no action.
  const staffCan = (loan, action) => Boolean(loan?.state?.staff_actions?.[action]);
  
  // Handle staff finalization and contract generation
  const handleGenerate = async (updatedClientData, loan) => {
    try {
      setLoading(true);
      
    // First, call staff-finalize to update the signatures and change status accordingly
      const staffFinalizeData = {
        lenderSignature: updatedClientData.lenderSignature,
        witness2Signature: updatedClientData.witness2Signature,
        initials: updatedClientData.initials,
        signatureLocations: updatedClientData.signatureLocations
      };
      
      // Call staff-finalize API endpoint
      const staffFinalizeResponse = await axios.post(
        apiUrl(`/api/loans/${loan.id}/staff-finalize`), 
        staffFinalizeData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      console.log('DEBUG: AdminDashboard - Staff finalize response:', staffFinalizeResponse.data);
      
    const newStatus = staffFinalizeResponse.data.loanStatus;
    if (newStatus === 'Awaiting Approval' || newStatus === 'Pending') {
        setAlert({
          type: 'success',
      message: `Staff signatures saved successfully. Loan status changed to ${newStatus}.`
        });
        
        // Refresh the loan list to show updated status
        try {
          await fetchLoans();
          await fetchAwaitingQueue();
        } catch (err) {
          console.error("Error refreshing loans:", err);
        }
        
        // Close the modal after successful staff finalization
        setShowLoanDetails(false);
        setSelectedLoan(null);
      } else {
        setAlert({
          type: 'error',
      message: 'Failed to update loan status'
        });
      }
    } catch (error) {
      console.error('Error finalizing staff signatures:', error);
      setAlert({
        type: 'error',
        message: error.response?.data?.message || 'An error occurred while submitting staff signatures'
      });
      
      // Don't close the modal on error - let the user fix their entries and try again
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionsPopupRef.current && !actionsPopupRef.current.contains(event.target)) {
        setShowActions({});
      }
    };
    if (Object.values(showActions).some(Boolean)) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActions]);

  // Fetch documents for a specific loan
  const fetchDocumentsForLoan = async (loanId) => {
    try {
      const token = localStorage.getItem('token');
      console.log(`Fetching documents for loan ID: ${loanId}`);
  const response = await axios.get(apiUrl(`/api/document/loan/${loanId}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Documents fetched:', response.data);
      return response.data?.documents || (Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching loan documents:', err);
      return [];
    }
  };
  
  // Fetch loans
  // silent: a background refresh — a passing network hiccup shows no error.
  const fetchLoans = async ({ silent = false } = {}) => {
    try {
      const token = localStorage.getItem('token');
  const response = await axios.get(apiUrl('/api/loans/'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLoans(response.data);
    } catch (err) {
      if (silent) return;
      setError('Error fetching loans: ' + err.message);
      throw err; // Rethrow so callers can catch it
    }
  };
  
  // Fetch admin reports (overdue invoices, totals)
  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl('/api/reports'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports({
        totalLoans: Number(res.data?.totalLoans || 0),
        overdueInvoices: Number(res.data?.overdueInvoices || 0),
      });
      setApiHealthy(true);
    } catch (err) {
      // If no permission to /api/reports, just mark API as reachable based on generic call
      setApiHealthy(false);
    }
  };
  
  // Fetch loans awaiting staff signature
  const fetchAwaitingQueue = async ({ silent = false } = {}) => {
    try {
      const token = localStorage.getItem('token');
  const res = await axios.get(apiUrl('/api/loans/awaiting-staff-signature'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAwaitingQueue(res.data || []);
      setTotalItems(res.data?.length || 0);
      setQueueError('');
    } catch (err) {
      if (silent) return; // keep what is shown
      setQueueError('Failed to fetch staff signature queue');
      setTotalItems(0);
    }
  };

  // Clients who signed up online themselves and are waiting to be verified:
  // registered (or resubmitted after being sent back) and not yet activated.
  // The longest waiting come first.
  const [verifyQueue, setVerifyQueue] = useState([]);
  const [verifyError, setVerifyError] = useState('');
  const fetchVerifyQueue = async ({ silent = false } = {}) => {
    if (!canOpenClientsPage) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl('/api/clients?page=1&pageSize=500&status=awaiting_verification'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      // The same rule again here, so an API that does not know the status yet
      // (and so returns every client) still lists only those waiting.
      const rows = (Array.isArray(res.data?.data) ? res.data.data : [])
        .filter((c) => c?.personal_info && !c.personal_info.is_active && !c.personal_info.awaiting_update);
      const waitingSince = (c) => new Date(c?.personal_info?.update_resubmitted_at || c?.personal_info?.created_at || 0).getTime() || 0;
      setVerifyQueue([...rows].sort((a, b) => waitingSince(a) - waitingSince(b)));
      setVerifyError('');
    } catch (err) {
      if (silent) return; // keep what is shown
      setVerifyError('Failed to fetch clients waiting to be verified');
    }
  };
  useEffect(() => { fetchVerifyQueue(); }, []);

  // The work queues keep themselves up to date: every 30 seconds while the
  // tab is visible, and straight away when the user comes back to it.
  const autoRefreshRef = useRef(null);
  autoRefreshRef.current = () => {
    if (document.visibilityState !== 'visible') return;
    fetchLoans({ silent: true });
    fetchAwaitingQueue({ silent: true });
    fetchVerifyQueue({ silent: true });
  };
  useEffect(() => {
    const tick = () => autoRefreshRef.current && autoRefreshRef.current();
    const timer = setInterval(tick, 30000);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', tick);
    };
  }, []);

  const fetchClientLimitStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl('/api/clients/limit-status'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClientLimitStatus(res.data);
    } catch (err) {
      // silent: limit status is optional
      setClientLimitStatus(null);
    }
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const token = localStorage.getItem('token');
  const response = await axios.get(apiUrl('/api/clients/'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClients(response.data);
      } catch (err) {
        setError('Error fetching clients: ' + err.message);
      }
    };
    fetchClients();
    fetchLoans();
    fetchAwaitingQueue();
    fetchClientLimitStatus();
  fetchReports();
  }, []);

  const refreshData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [clientsRes, loansRes] = await Promise.all([
  axios.get(apiUrl('/api/clients/'), {
          headers: { Authorization: `Bearer ${token}` },
        }),
  axios.get(apiUrl('/api/loans/'), {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setClients(clientsRes.data);
      setLoans(loansRes.data);
      fetchClientLimitStatus();
  fetchReports();
    } catch (err) {
      // silent
    }
  };

  const cancelLoan = async (loanId, reason = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(apiUrl(`/api/loans/${loanId}/cancel`), { cancel_reason: reason }, { headers: { Authorization: `Bearer ${token}` } });
      setActionMessage('Loan cancelled');
      setActionType('warning');
      await refreshData();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Error cancelling loan';
      setActionMessage(msg);
      setActionType('error');
    }
  };

  const terminateContract = async (loanId, note = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(apiUrl(`/api/loans/${loanId}/terminate`), { termination_note: note }, { headers: { Authorization: `Bearer ${token}` } });
      setActionMessage('Contract terminated');
      setActionType('warning');
      await refreshData();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Error terminating contract';
      setActionMessage(msg);
      setActionType('error');
    }
  };

  const writeOffLoan = async (loanId, note = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(apiUrl(`/api/loans/${loanId}/write-off`), { note }, { headers: { Authorization: `Bearer ${token}` } });
      setActionMessage('Loan written off');
      setActionType('success');
      await refreshData();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Failed to write off loan';
      setActionMessage(msg);
      setActionType('error');
      throw new Error(msg);
    }
  };

  const undoWriteOffLoan = async (loanId, note = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(apiUrl(`/api/loans/${loanId}/undo-write-off`), { note }, { headers: { Authorization: `Bearer ${token}` } });
      setActionMessage('Loan write-off undone');
      setActionType('success');
      await refreshData();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Failed to undo write-off';
      setActionMessage(msg);
      setActionType('error');
      throw new Error(msg);
    }
  };

  const revertLoanToPending = async (loanId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(apiUrl(`/api/loans/${loanId}/revert-to-pending`), {}, { headers: { Authorization: `Bearer ${token}` } });
      setActionMessage('Loan status changed to pending');
      setActionType('success');
      await refreshData();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Error changing loan status';
      setActionMessage(msg);
      setActionType('error');
    }
  };

  // The repayment to undo is the API's choice (state.staff_actions.undo_repayment).
  const getUndoTarget = (loan) => {
    const undo = loan?.state?.staff_actions?.undo_repayment;
    if (!loan?.id || !undo) return null;
    const prettyDate = undo.payment_date ? formatDateDMY(undo.payment_date) : '';
    return { repaymentId: undo.id, amount: Number(undo.amount) || 0, displayDate: prettyDate };
  };

  const undoRepayment = async (loanId, repaymentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        apiUrl(`/api/loans/${loanId}/repayments/${repaymentId}/undo`),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActionMessage('Repayment undone');
      setActionType('success');
      await refreshData();
    } catch (e) {
      const data = e?.response?.data;
      const baseMsg = data?.error || data?.message || e?.message || 'Could not undo repayment.';
      setActionMessage(baseMsg);
      setActionType('error');
      throw new Error(baseMsg);
    }
  };

  const openConfirm = (kind, loan, extra = {}) => {
    setConfirmCtx({ kind, loan, extra });
  };

  const closeConfirm = () => setConfirmCtx(null);

  // body: { approve_reason } when the affordability check declined or referred it.
  const approveLoan = async (loanId, body = {}) => {
    try {
      const token = localStorage.getItem('token');
  await axios.put(apiUrl(`/api/loans/${loanId}/approve`), body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActionMessage('Loan approved');
      setActionType('success');
      refreshData();
    } catch (error) {
      setActionMessage(error?.response?.data?.error || error?.response?.data?.message || 'Error approving loan');
      setActionType('error');
      // LoanDetails shows the API's reason and stays open.
      throw error;
    }
  };

  const rejectLoan = async (loanId, rejection_reason) => {
    try {
      const token = localStorage.getItem('token');
  await axios.put(apiUrl(`/api/loans/${loanId}/reject`), { rejection_reason }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActionMessage('Loan rejected');
      setActionType('error');
      refreshData();
    } catch (error) {
      setActionMessage(error?.response?.data?.error || error?.response?.data?.message || 'Error rejecting loan');
      setActionType('error');
    }
  };

  // Only show clients with loans
  const loansWithClient = loans.map(loan => {
    const client = clients.find(c => c.personal_info && c.personal_info.id === loan.client_id);
    let clientName = 'Unknown';
    if (client && client.personal_info) {
      const { first_name, last_name } = client.personal_info;
      clientName = [first_name, last_name].filter(Boolean).join(' ');
      if (!clientName.trim()) clientName = 'Unknown';
    }
    return {
      ...loan,
      clientName,
      repayments: loan.repayments || [],
      clientId: loan.client_id,
    };
  });

  const now = new Date();
  const rangeStart = new Date(now.getFullYear(), now.getMonth() - monthsWindow + 1, 1);
  const isInRange = (dateVal) => {
    if (!dateVal) return false;
    const d = new Date(dateVal);
    return !isNaN(d.getTime()) && d >= rangeStart;
  };

  const loansInRange = loans.filter(l => isInRange(l.created_at));
  // paid_out_on is the API's 'YYYY-MM-DD' approval (payout) date, null when
  // the loan was never paid out. Read as a local date, so the month is right.
  const paidOutDate = (l) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(l?.paid_out_on || ''));
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
  };
  const wasPaidOut = (l) => Boolean(l?.state?.approved) || l?.paid_out_on != null;
  // Loans paid out (approved) in the period: by paid_out_on, not created_at.
  const loansPaidOutInRange = loans.filter(l => {
    if (!wasPaidOut(l)) return false;
    const d = paidOutDate(l);
    return Boolean(d) && d >= rangeStart;
  });
  const loansWithClientInRange = loansWithClient.filter(l => isInRange(l.created_at));
  const clientsInRange = clients.filter(c => isInRange(c?.personal_info?.created_at));

  // Filter by search (show all if searchTerm is empty)
  const filteredLoans = loansWithClientInRange.filter(loan => {
    if (!loan.clientName || typeof loan.clientName !== 'string') return false;
    if (!searchTerm) return true;
    return loan.clientName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const totalPages = Math.ceil(filteredLoans.length / PAGE_SIZE);
  const paginatedLoans = filteredLoans.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Dashboard stats (range-aware)
  // New clients: clients created in the period (all clients shown beside it).
  const newClients = clientsInRange.length;
  const allClientsCount = clients.length;
  // Approved loans: every loan paid out in the period (paid_out_on), whatever
  // happened to it since (repaid, bad debt, written off) - state.approved.
  const totalApprovedLoans = loansPaidOutInRange.length;
  // Rejected loans: applications created in the period that were rejected.
  const totalRejectedLoans = loansInRange.filter(l => l.status === 'Rejected').length;
  // Sum repayments amounts within range window (by repayment date)
  const totalRepayments = loans.reduce((sum, loan) => {
    if (!Array.isArray(loan.repayments)) return sum;
    const add = loan.repayments.reduce((s, r) => {
      const d = new Date(r.date || r.payment_date);
      if (isNaN(d.getTime()) || d < rangeStart) return s;
      const amt = Number(r.amount ?? r.amount_paid ?? 0);
      return !isNaN(amt) ? s + amt : s;
    }, 0);
    return sum + add;
  }, 0);

  // Admin KPIs
  // Work queue: everything waiting now, not limited by the date window.
  const approvalsPendingCount = loans.filter(l => l.status === 'Awaiting Approval').length;
  // Σ principal of the loans paid out in the period (paid_out_on).
  const totalDistributedAmount = loansPaidOutInRange.reduce((sum, l) => {
    const num = Number(l.principal_amount ?? 0);
    return sum + (!isNaN(num) && num > 0 ? num : 0);
  }, 0);
  // Instalments due in the next 7 days (GET /api/loans/due-soon).
  const [dueSoon, setDueSoon] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl('/api/loans/due-soon'), { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setDueSoon(res.data || null);
      } catch {
        if (!cancelled) setDueSoon({ count: 0, amount: 0, loans: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // Overdue loans: the API's call (loan.state.is_overdue — an instalment is
  // late), over ALL loans: overdue now, whenever the loan was made.
  const overdueCount = loans.filter(l => l.state?.is_overdue === true).length;

  // Overdue priority list (paginated)
  // Days overdue come from the API (loan.state.days_overdue) when the loan
  // carries its state.
  // A work queue: every overdue loan now, not limited by the date window.
  const overduePriority = loansWithClient
    .filter(l => l.state?.is_overdue === true)
    .map(l => {
      const diffDays = Number(l.state.days_overdue) || 0;
      return {
        id: l.id,
        loan_code: l.loan_code || l.loanCode,
        client_id: l.client_id,
        client_code: l.client_code || l.clientCode,
        clientName: l.clientName,
        start_date: l.start_date || l.startDate || l.created_at,
        due_date: l.due_date,
        daysOverdue: diffDays,
        totalDue: Number(l.total_due ?? 0),
        status: l.status,
      };
    })
    .filter(x => x.daysOverdue > 0 && x.totalDue > 0)
    .sort((a, b) => b.daysOverdue - a.daysOverdue)
    .slice();

  const overdueTotalPages = Math.ceil(overduePriority.length / OVERDUE_PAGE_SIZE) || 1;
  const overduePageRows = overduePriority.slice((overduePage - 1) * OVERDUE_PAGE_SIZE, overduePage * OVERDUE_PAGE_SIZE);

  useEffect(() => {
    // Keep overdue pagination in bounds if dataset changes
    setOverduePage(prev => {
      const maxPage = Math.ceil(overduePriority.length / OVERDUE_PAGE_SIZE) || 1;
      return Math.min(Math.max(prev, 1), maxPage);
    });
  }, [overduePriority.length]);

  // Work queue: everything awaiting approval now, not window-limited.
  const awaitingApproval = loansWithClient.filter(l => l.status === 'Awaiting Approval');
  const loanText = (l) => `${l?.loan_code || l?.loanCode || ''} ${l?.clientName || ''}`;
  const awaitingView = useQueueView(awaitingQueue, (l) => l.client_signed_at, loanText);
  const approvalView = useQueueView(awaitingApproval, (l) => l.updated_at, loanText);
  // Loans the verification calculator declined that are not yet approved
  // (still before approval, not rejected or cancelled), oldest first: they go
  // ahead only on an assessor's reason, so they wait on someone.
  const BEFORE_APPROVAL = ['Pending', 'Awaiting Client', 'Awaiting Staff', 'Awaiting Approval'];
  const calculatorDeclined = loansWithClient
    .filter((l) => l.calculator?.declined && BEFORE_APPROVAL.includes(l.status))
    .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
  const declinedView = useQueueView(calculatorDeclined, (l) => l.created_at, loanText);
  const overdueView = useQueueView(overduePriority, (l) => l.due_date, loanText);
  const verifyView = useQueueView(
    verifyQueue,
    (c) => c?.personal_info?.update_resubmitted_at || c?.personal_info?.created_at,
    (c) => {
      const p = c?.personal_info || {};
      return [p.full_name, p.client_code, p.id_passport_no, p.email, p.phone_number].filter(Boolean).join(' ');
    },
  );

  // Chart helpers
  const getLastNMonths = (n) => {
    const months = [];
    const now = new Date();
    for (let i = n - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(d.toLocaleString('default', { month: 'short', year: '2-digit' }));
    }
    return months;
  };

  const monthLabels = getLastNMonths(monthsWindow);

  const getMonthlyCounts = (items, dateField, filterFn) => {
    const counts = Array(monthsWindow).fill(0);
    const now = new Date();
    items.forEach(item => {
      // Defensive check for nested date fields
      let dateVal = item[dateField];
      if (dateField.includes('.')) {
        const [obj, field] = dateField.split('.');
        dateVal = item[obj] ? item[obj][field] : undefined;
      }
      if (!dateVal) return;
      const date = new Date(dateVal);
      if (isNaN(date.getTime())) return;
      const diffMonths = (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());
      if (diffMonths >= 0 && diffMonths < monthsWindow && (!filterFn || filterFn(item))) {
        counts[(monthsWindow - 1) - diffMonths] += 1;
      }
    });
    return counts;
  };

  const monthlyClients = getMonthlyCounts(clients, 'personal_info.created_at');
  // Approved loans per month of payout (paid_out_on), as the tile counts.
  const monthlyApprovedLoans = (() => {
    const counts = Array(monthsWindow).fill(0);
    loans.forEach(l => {
      if (!wasPaidOut(l)) return;
      const d = paidOutDate(l);
      if (!d) return;
      const diffMonths = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
      if (diffMonths >= 0 && diffMonths < monthsWindow) counts[(monthsWindow - 1) - diffMonths] += 1;
    });
    return counts;
  })();
  const monthlyRejectedLoans = getMonthlyCounts(
    loans,
    'created_at',
    l => l.status === 'Rejected'
  );
  // FIX: Sum repayments per month for monthlyRepayments
  const monthlyRepayments = (() => {
    const sums = Array(monthsWindow).fill(0);
    const now = new Date();
    loans.forEach(loan => {
      if (loan.repayments && Array.isArray(loan.repayments)) {
        loan.repayments.forEach(r => {
          const date = new Date(r.date || r.payment_date);
          if (isNaN(date.getTime())) return;
          const diffMonths = (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());
          if (diffMonths >= 0 && diffMonths < monthsWindow) {
            const val = Number(r.amount ?? r.amount_paid);
            if (!isNaN(val)) sums[(monthsWindow - 1) - diffMonths] += val;
          }
        });
      }
    });
    return sums;
  })();

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index', intersect: false },
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, grid: { color: '#e5e7eb' } },
    },
  };

  const clientsChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'New clients',
        data: monthlyClients,
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37,99,235,0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const approvedLoansChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Approved Loans',
        data: monthlyApprovedLoans,
        borderColor: '#22c55e',
        backgroundColor: 'rgba(34,197,94,0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const rejectedLoansChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Rejected Loans',
        data: monthlyRejectedLoans,
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239,68,68,0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const repaymentsChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Repayments',
        data: monthlyRepayments,
        borderColor: '#eab308',
        backgroundColor: 'rgba(234,179,8,0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const handleShowActions = (loanId) => {
    setShowActions(prev => ({
      ...Object.fromEntries(Object.keys(prev).map(id => [id, false])),
      [loanId]: !prev[loanId],
    }));
  };

  const handleCloseActions = () => setShowActions({});

  const handleShowLoanDetails = async (loan, source = 'other') => {
    const documents = await fetchDocumentsForLoan(loan.id);
    setLoanDocuments(documents);
    setSelectedLoan({...loan, documents});
    setLoanDetailsSource(source);
    setShowInvoiceModal(false); setShowLoanDetails(true);
    handleCloseActions();
  };

  // A loan in one of the queues (its card, code or Review/View button).
  const openQueueLoan = async (item, source) => {
    try {
      const documents = await fetchDocumentsForLoan(item.id);
      setLoanDocuments(documents);
      // The full loan, as every Loan Details screen gets it: what staff may do
      // with it (state), the calculator and the document flags. The queue's
      // own fields (the client's signatures and references) are kept.
      let full = {};
      try {
        const res = await axios.get(apiUrl(`/api/loans/${item.id}`), { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        full = res.data || {};
      } catch { /* the queue's copy is used as it is */ }
      setSelectedLoan({ ...full, ...item, state: full.state || item.state, calculator: full.calculator ?? item.calculator, document_flags: full.document_flags ?? item.document_flags, documents });
      if (source) setLoanDetailsSource(source);
      setShowInvoiceModal(false); setShowLoanDetails(true);
    } catch (e) {
      console.error('Error opening details:', e);
    }
  };

  const handleShowOverdueLoanDetails = async (row) => {
    const fullLoan = Array.isArray(loans) ? loans.find(l => l?.id === row?.id) : null;
    const fallback = {
      id: row?.id,
      client_id: row?.client_id,
      clientName: row?.clientName,
      status: row?.status,
    };
    await handleShowLoanDetails(fullLoan || fallback, 'overdue');
  };

  const handleCloseLoanDetails = () => {
    setShowLoanDetails(false);
    setSelectedLoan(null);
    setLoanDetailsSource('');
  };

  const handleShowRepaymentForm = (loan, clientId) => {
    setRepaymentLoan(loan);
    setRepaymentClientId(clientId);
    setShowRepaymentForm(true);
    handleCloseActions();
  };

  const handleCloseRepaymentForm = () => {
    setShowRepaymentForm(false);
    setRepaymentLoan(null);
    setRepaymentClientId(null);
  };

  const handleShowEditForm = (loan) => {
    setEditLoan(loan);
    setShowEditForm(true);
    handleCloseActions();
  };

  const handleCloseEditForm = () => {
    setShowEditForm(false);
    setEditLoan(null);
  };

  const canViewInvoice = (loan) => Boolean(loan?.state?.actions?.view_invoice);

  const openInvoice = async (loan) => {
    // Only one in-page panel at a time
    setShowLoanDetails(false);
    setSelectedLoan(null);
    try {
      const enriched = await enrichLoanForInvoice(loan);
      setInvoiceLoan(enriched);
      setShowInvoiceModal(true);
    } catch {
      setInvoiceLoan(loan);
      setShowInvoiceModal(true);
    }
  };

  const getMessageClass = () => {
    switch (actionType) {
      case 'success':
        return 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700/50';
      case 'error':
        return 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700/50';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700/50';
      default:
        return 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700/50';
    }
  };

  return (
    <div className="max-w-full min-h-screen">

      {/* The NAMFISA licence: from 90 days before it expires, or once expired */}
      <LicenceAlert />

      {/* Documents flagged as possibly tampered with, waiting for review */}
      <FlaggedDocumentsCard />

      {/* Loan applications this user saved to finish later */}
      <StaffSavedApplications />

      {/* Health + Actions */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${apiHealthy ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-400'}`}>
          {apiHealthy ? 'API Online' : 'Reports Unavailable'}
        </span>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600 dark:text-slate-400">Range</label>
          <div className="inline-flex rounded-md border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800 overflow-hidden">
            <Button
              variant={monthsWindow === 3 ? 'primary' : 'secondary'}
              size="sm"
              className="rounded-none"
              onClick={() => setMonthsWindow(3)}
            >
              Last 3 months
            </Button>
            <Button
              variant={monthsWindow === 6 ? 'primary' : 'secondary'}
              size="sm"
              className="rounded-none border-l border-gray-200"
              onClick={() => setMonthsWindow(6)}
            >
              Last 6 months
            </Button>
            <Button
              variant={monthsWindow === 12 ? 'primary' : 'secondary'}
              size="sm"
              className="rounded-none border-l border-gray-200"
              onClick={() => setMonthsWindow(12)}
            >
              Last 12 months
            </Button>
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={overdueTriggering}
          busyLabel="Running…"
          onClick={async () => {
            try {
              setOverdueTriggering(true);
              const token = localStorage.getItem('token');
              if (!token) {
                setActionMessage('(401) No token provided');
                setActionType('error');
                return;
              }

              await axios.post(
                apiUrl('/api/test-overdue-loans'),
                {},
                { headers: { Authorization: `Bearer ${token}` } }
              );
              setActionMessage('Overdue processor ran successfully');
              setActionType('success');
              await refreshData();
            } catch (e) {
              const status = e?.response?.status;
              const serverError = e?.response?.data?.error || e?.response?.data?.message;
              const details = e?.response?.data?.details;
              const fallback = e?.message || 'Failed to run overdue processor';
              const msgBase = serverError || fallback;
              const statusPrefix = status ? `(${status}) ` : '';
              const detailsSuffix = details ? `: ${details}` : '';
              setActionMessage(`${statusPrefix}${msgBase}${detailsSuffix}`);
              setActionType('error');
            } finally {
              setOverdueTriggering(false);
            }
          }}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
          }
        >
          {overdueTriggering ? 'Running…' : 'Run Overdue Processor'}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            setBulkResult(null);
            setShowBulkUpload(true);
          }}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
          }
        >
          Bulk Payment Upload
        </Button>
        {canCollectDebitOrders && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowDebitOrders(true)}
            title="Download the debit order collection file (EnDO) for chosen dates"
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
              </svg>
            }
          >
            Debit Order File
          </Button>
        )}
      </div>
      {showDebitOrders && <DebitOrderBatchModal onClose={() => setShowDebitOrders(false)} />}

      {/* Quick KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <KpiCard label="Total Distributed Loans" value={toCurrency(totalDistributedAmount)} color="blue"
          subtitle={`Principal paid out in the period · ${totalApprovedLoans} loan${totalApprovedLoans === 1 ? '' : 's'}`}
          onClick={tileGo(canOpenLoansPage, '/manage-loans?status=Approved')} title="Show the approved (paid out) loans" />
        <KpiCard label="Overdue Loans" value={overdueCount} color="red" subtitle="An instalment is late · all dates"
          onClick={tileGo(canOpenLoansPage, '/manage-loans?status=Overdue')} title="Show the overdue loans" />
        <KpiCard label="Approvals Pending" value={approvalsPendingCount} color="amber" subtitle="Waiting now · all dates"
          onClick={tileGo(canOpenLoansPage, '/manage-loans?status=Awaiting%20Approval')} title="Show the loans awaiting approval" />
        <KpiCard label="Due in Next 7 Days" value={toCurrency(dueSoon?.amount || 0)} color="green"
          subtitle={dueSoon ? `${dueSoon.count} loan${dueSoon.count === 1 ? '' : 's'}` : '…'}
          onClick={tileGo(canOpenLoansPage, '/manage-loans?status=Due%20Soon')} title="Show the loans with an instalment due in the next 7 days" />
      </div>

      {clientLimitStatus?.limitEnabled && (clientLimitStatus.reached || (Number(clientLimitStatus.remaining) <= Number(clientLimitStatus.warningThreshold ?? 10))) && (
        <div
          className={
            (() => {
              const remaining = Number(clientLimitStatus.remaining);
              if (clientLimitStatus.reached || remaining <= 0) return 'mb-4 p-3 rounded-lg border border-red-300 dark:border-red-700/50 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
              if (remaining <= 2) return 'mb-4 p-3 rounded-lg border border-red-200 dark:border-red-700/50 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300';
              if (remaining <= 5) return 'mb-4 p-3 rounded-lg border border-orange-300 dark:border-orange-700/50 bg-orange-100 dark:bg-orange-900/30 text-orange-950 dark:text-orange-300';
              return 'mb-4 p-3 rounded-lg border border-orange-200 dark:border-orange-700/40 bg-orange-50 dark:bg-orange-900/20 text-orange-900 dark:text-orange-300';
            })()
          }
          role="alert"
        >
          {clientLimitStatus.reached ? (
            <span>
              Reached client limit ({clientLimitStatus.clientCount}/{clientLimitStatus.limit}). You cannot add new clients.
            </span>
          ) : (
            <span>
              Client limit warning: {clientLimitStatus.remaining} clients remaining ({clientLimitStatus.clientCount}/{clientLimitStatus.limit}).
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <ChartCard label="New Clients" value={newClients} color="blue" subtitle={`Registered in the period · ${allClientsCount} in total`}
          onClick={tileGo(canOpenClientsPage, '/manage-clients')} title="Open Clients">
          <Line data={clientsChartData} options={chartOptions} height={80} />
        </ChartCard>
        <ChartCard label="Approved Loans" value={totalApprovedLoans} color="green" subtitle="Paid out in the period"
          onClick={tileGo(canOpenLoansPage, '/manage-loans?status=Approved')} title="Show the approved loans">
          <Line data={approvedLoansChartData} options={chartOptions} height={80} />
        </ChartCard>
        <ChartCard label="Repayments Made" value={toCurrency(totalRepayments)} color="amber"
          onClick={tileGo(canOpenRepaymentsPage, `/repayments?from=${ymd(rangeStart)}`)} title="Show the repayments in this period">
          <Line data={repaymentsChartData} options={chartOptions} height={80} />
        </ChartCard>
        <ChartCard label="Rejected Loans" value={totalRejectedLoans} color="red" subtitle="Applied in the period, rejected"
          onClick={tileGo(canOpenLoansPage, '/manage-loans?status=Rejected')} title="Show the rejected loans">
          <Line data={rejectedLoansChartData} options={chartOptions} height={80} />
        </ChartCard>
      </div>

      {actionMessage && (
        <div className={`mb-4 p-3 rounded border flex items-center justify-between ${getMessageClass()}`}>
          <span>{actionMessage}</span>
          <Button
            onClick={() => setActionMessage('')}
            variant="text"
            size="sm"
            className="ml-4 !p-1 rounded-full"
            aria-label="Close"
            title="Close"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            }
          />
          
        </div>
      )}

      {error && <div className="p-3 rounded-lg mb-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">{error}</div>}
      {alert.message && (
        <div className={`p-3 rounded-lg mb-4 ${alert.type === 'success' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'}`}>
          {alert.message}
        </div>
      )}

      {/* Loan queues / overdue tables — hidden while an in-page form or invoice
          panel is open so the panel takes the full content width. The stat cards
          above stay visible. */}
      {!(showLoanDetails || showInvoiceModal) && (
      <>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-6 items-start">
      <QueuePanel
        title="Awaiting Staff Signature"
        tone="yellow"
        count={awaitingView.filtering ? `${awaitingView.total}/${totalItems}` : totalItems}
        toolbar={<QueueToolbar view={awaitingView} dateLabel="Signed" />}
        right={
          <div className="flex items-center gap-1">
            <Pager compact page={awaitingView.page} pages={awaitingView.pages} onPage={awaitingView.setPage} />
            <ActionButton onClick={fetchAwaitingQueue} busyLabel="…" title="Refresh"
              className="p-1 rounded text-slate-600 dark:text-slate-300 hover:bg-white/70 dark:hover:bg-slate-700">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </ActionButton>
          </div>
        }
      >
        {queueError && <div className="p-2 text-xs bg-red-50 text-red-700 rounded">{queueError}</div>}
        {awaitingView.rows.length === 0 ? (
          <QueueEmpty>{awaitingView.filtering ? 'Nothing matches.' : 'No items in the queue.'}</QueueEmpty>
        ) : (
          <ResponsiveTable
            data={awaitingView.rows}
            compact
            onRowClick={(item) => openQueueLoan(item, 'awaiting-staff')}
            columns={[
              queueLoanCol((item) => openQueueLoan(item, 'awaiting-staff')),
              { field: 'client_signed_at', header: 'Submitted', formatter: (v) => formatDateDMY(v) },
              { field: 'actions', header: 'Actions', formatter: (_, item) => (
              <ActionButton busyLabel="…" onClick={() => openQueueLoan(item, 'awaiting-staff')}>Review</ActionButton>
            ) },
            ]}
          />
        )}
      </QueuePanel>

      <QueuePanel
        title="Awaiting Approval"
        tone="indigo"
        count={approvalView.filtering ? `${approvalView.total}/${(awaitingApproval || []).length}` : (awaitingApproval || []).length}
        toolbar={<QueueToolbar view={approvalView} dateLabel="Updated" />}
        right={<Pager compact page={approvalView.page} pages={approvalView.pages} onPage={approvalView.setPage} />}
      >
        {approvalView.rows.length === 0 ? (
          <QueueEmpty>{approvalView.filtering ? 'Nothing matches.' : 'No loans awaiting approval.'}</QueueEmpty>
        ) : (
          <ResponsiveTable
            data={approvalView.rows}
            compact
            onRowClick={(item) => openQueueLoan(item)}
            columns={[
              queueLoanCol((item) => openQueueLoan(item)),
              { field: 'updated_at', header: 'Updated', formatter: (v) => formatDateDMY(v) },
              { field: 'actions', header: 'Actions', formatter: (_, item) => (
              <ActionButton busyLabel="…" onClick={() => openQueueLoan(item)}>View Details</ActionButton>
            ) },
            ]}
          />
        )}
      </QueuePanel>

      {/* Loans the calculator declined that are still waiting, not yet approved */}
      <QueuePanel
        title="Declined by Calculator, Not Approved"
        tone="rose"
        count={declinedView.filtering ? `${declinedView.total}/${calculatorDeclined.length}` : calculatorDeclined.length}
        toolbar={<QueueToolbar view={declinedView} dateLabel="Applied" />}
        right={<Pager compact page={declinedView.page} pages={declinedView.pages} onPage={declinedView.setPage} />}
      >
        {declinedView.rows.length === 0 ? (
          <QueueEmpty>{declinedView.filtering ? 'Nothing matches.' : 'No declined loans waiting.'}</QueueEmpty>
        ) : (
          <ResponsiveTable
            data={declinedView.rows}
            compact
            onRowClick={(item) => openQueueLoan(item)}
            columns={[
              queueLoanCol((item) => openQueueLoan(item), false),
              {
                field: 'principal_amount',
                header: 'Asked',
                formatter: (v, item) => (
                  <div className="whitespace-nowrap">
                    {Number(v) > 0 ? toCurrency(v) : '-'}
                    <div className="text-[11px] text-gray-500 dark:text-slate-400">Applied {formatDateDMY(item.created_at)}</div>
                  </div>
                ),
              },
              { field: 'actions', header: 'Actions', formatter: (_, item) => (
              <ActionButton busyLabel="…" onClick={() => openQueueLoan(item)}>Review</ActionButton>
            ) },
            ]}
          />
        )}
      </QueuePanel>

      {/* Clients who signed up online, waiting for staff to verify (activate) them */}
      {canOpenClientsPage && (
        <QueuePanel
          title="Online Sign-ups Awaiting Verification"
          tone="emerald"
          count={verifyView.filtering ? `${verifyView.total}/${verifyQueue.length}` : verifyQueue.length}
          toolbar={<QueueToolbar view={verifyView} dateLabel="Waiting" placeholder="Name, code, ID or phone" />}
          right={<Pager compact page={verifyView.page} pages={verifyView.pages} onPage={verifyView.setPage} />}
        >
          {verifyError && <div className="p-2 text-xs bg-red-50 text-red-700 rounded">{verifyError}</div>}
          {verifyView.rows.length === 0 ? (
            <QueueEmpty>{verifyView.filtering ? 'Nothing matches.' : 'No online sign-ups waiting to be verified.'}</QueueEmpty>
          ) : (
            <ResponsiveTable
              data={verifyView.rows.map((c) => ({ ...c, id: c.personal_info?.id }))}
              compact
              onRowClick={(c) => navigate(`/manage-clients?client=${c.personal_info?.id}`)}
              columns={[
                {
                  field: 'name',
                  header: 'Client',
                  formatter: (_, c) => {
                    const pi = c.personal_info || {};
                    const name = pi.full_name || [pi.first_name, pi.middle_name, pi.last_name].filter(Boolean).join(' ') || '-';
                    // The code under the name; resubmitted ones tagged.
                    return (
                      <div>
                        <ClientLink clientId={pi.id} name={name} />
                        <div className="text-[11px] text-gray-500 dark:text-slate-400">{pi.client_code || pi.id_passport_no || ''}</div>
                        {pi.update_resubmitted_at && <StatusBadge status="Resubmitted" size="small" className="mt-0.5" />}
                      </div>
                    );
                  },
                },
                {
                  field: 'waiting',
                  header: 'Since',
                  formatter: (_, c) => formatDateDMY(c.personal_info?.update_resubmitted_at || c.personal_info?.created_at),
                },
                {
                  field: 'actions',
                  header: 'Actions',
                  formatter: (_, c) => (
                    <button type="button" onClick={() => navigate(`/manage-clients?client=${c.personal_info?.id}`)}>Review</button>
                  ),
                },
              ]}
            />
          )}
        </QueuePanel>
      )}

      {/* Write-off candidates (policy and prescription; never automatic) */}
      {canOpenLoansPage && (
        <WriteOffCandidatesPanel
          className="mb-4"
          refreshKey={writeOffRefresh}
          canWriteOff={authPerms.includes('*') || authPerms.includes('ManageLoans')}
          onWriteOff={(c, note) => openConfirm('writeOff', { id: c.loan_id, loan_code: c.loan_code }, { note })}
        />
      )}

      {/* Overdue Priority List */}
      <QueuePanel
        title="Overdue Priority"
        tone="red"
        count={overdueView.filtering ? `${overdueView.total}/${overduePriority.length}` : overduePriority.length}
        toolbar={<QueueToolbar view={overdueView} dateLabel="Due" />}
        right={<Pager compact page={overdueView.page} pages={overdueView.pages} onPage={overdueView.setPage} />}
      >
        <div>
          {overdueView.rows.length === 0 ? (
            <QueueEmpty>{overdueView.filtering ? 'Nothing matches.' : 'No overdue items.'}</QueueEmpty>
          ) : (
            <ResponsiveTable
              data={overdueView.rows}
            compact
              onRowClick={(row) => handleShowOverdueLoanDetails(row)}
              columns={[
                queueLoanCol((row) => handleShowOverdueLoanDetails(row), false),
                {
                  // What is owed, with how late it is under it.
                  field: 'totalDue',
                  header: 'Total Due',
                  formatter: (v, row) => {
                    const fullLoan = (Array.isArray(loansWithClient) ? loansWithClient.find(l => l?.id === row?.id) : null) || row;
                    return (
                      <div className="whitespace-nowrap">
                        <LoanLink
                          loan={row}
                          onOpen={() => (canViewInvoice(fullLoan) ? openInvoice(fullLoan) : handleShowOverdueLoanDetails(row))}
                          title={canViewInvoice(fullLoan) ? 'Open the invoice' : 'Open loan details'}
                        >
                          {toCurrency(v)}
                        </LoanLink>
                        <div className="text-[11px] font-semibold text-red-700 dark:text-red-400">{row.daysOverdue} days late</div>
                      </div>
                    );
                  },
                },
                {
                  field: 'actions',
                  header: 'Actions',
                  formatter: (_, row) => {
                    const fullLoan = (Array.isArray(loansWithClient) ? loansWithClient.find(l => l?.id === row?.id) : null) || row;
                    return (
                      <>
              <ActionButton
                onClick={() => handleShowOverdueLoanDetails(row)}
                className="px-2.5 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 transition-colors"
                busyLabel="Loading…"
              >
                View Details
              </ActionButton>

              {canViewInvoice(fullLoan) && (
                <ActionButton
                  onClick={() => openInvoice(fullLoan)}
                  className="px-2.5 py-1 text-xs font-medium rounded-md bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 transition-colors"
                  busyLabel="Loading…"
                >
                  View Invoice
                </ActionButton>
              )}

              {staffCan(fullLoan, 'enter_repayment') && (
                <button
                  onClick={() => handleShowRepaymentForm(fullLoan, fullLoan.clientId || fullLoan.client_id)}
                  className="px-2.5 py-1 text-xs font-medium rounded-md bg-purple-50 text-purple-600 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-300 transition-colors"
                >
                  Enter Repayment
                </button>
              )}

              {staffCan(fullLoan, 'edit') && (
                <button
                  onClick={() => handleShowEditForm(fullLoan)}
                  className="px-2.5 py-1 text-xs font-medium rounded-md bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 transition-colors"
                >
                  Edit Loan
                </button>
              )}

              {staffCan(fullLoan, 'cancel') && (
                <button
                  onClick={() => openConfirm('cancel', fullLoan)}
                  className="px-2.5 py-1 text-xs font-medium rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300 transition-colors"
                >
                  Cancel Loan
                </button>
              )}

              {staffCan(fullLoan, 'terminate') && (
                <button
                  onClick={() => openConfirm('terminate', fullLoan)}
                  className="px-2.5 py-1 text-xs font-medium rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300 transition-colors"
                >
                  Terminate Contract
                </button>
              )}

              {staffCan(fullLoan, 'write_off') && (
                <button
                  onClick={() => openConfirm('writeOff', fullLoan)}
                  className="px-2.5 py-1 text-xs font-medium rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300 transition-colors"
                >
                  Write Off
                </button>
              )}

              {staffCan(fullLoan, 'undo_write_off') && (
                <button
                  onClick={() => openConfirm('undoWriteOff', fullLoan)}
                  className="px-2.5 py-1 text-xs font-medium rounded-md bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 transition-colors"
                >
                  Undo Write-Off
                </button>
              )}

              {staffCan(fullLoan, 'revert_to_pending') && (
                <button
                  onClick={() => openConfirm('revertToPending', fullLoan)}
                  className="px-2.5 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 transition-colors"
                >
                  Revert to Pending
                </button>
              )}

              {staffCan(fullLoan, 'undo_repayment') && (
                <button
                  onClick={() => {
                    const target = getUndoTarget(fullLoan);
                    if (!target) {
                      setActionMessage('No repayments to undo for this loan');
                      setActionType('error');
                    } else {
                      setUndoCtx({ loanId: fullLoan.id, loanCode: fullLoan.loan_code, repaymentId: target.repaymentId });
                    }
                  }}
                  className="px-2.5 py-1 text-xs font-medium rounded-md bg-purple-50 text-purple-600 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-300 transition-colors"
                >
                  Undo Repayment
                </button>
              )}
              {Number(fullLoan?.undone_repayments) > 0 && staffCan(fullLoan, 'enter_repayment') && (
                <button
                  onClick={() => setUndoCtx({ loanId: fullLoan.id, loanCode: fullLoan.loan_code, mode: 'redo' })}
                  className="px-2.5 py-1 text-xs font-medium rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 transition-colors"
                >
                  Redo Repayment
                </button>
              )}

                      </>
                    );
                  },
                },
              ]}
            />
          )}
        </div>
      </QueuePanel>
      </div>
      </>
      )}

      {undoCtx && (
        <UndoRepaymentConfirm
          ctx={undoCtx}
          mode={undoCtx.mode || 'undo'}
          onClose={() => setUndoCtx(null)}
          onSuccess={async () => {
            const redone = undoCtx.mode === 'redo';
            setUndoCtx(null);
            setActionMessage(redone ? 'Repayment put back' : 'Repayment undone');
            setActionType('success');
            await refreshData();
          }}
        />
      )}
      <ConfirmActionModal
        open={Boolean(confirmCtx)}
        title={(() => {
          if (!confirmCtx) return '';
          if (confirmCtx.kind === 'writeOff') return 'Write off loan';
          if (confirmCtx.kind === 'undoWriteOff') return 'Undo write-off';
          if (confirmCtx.kind === 'terminate') return 'Terminate contract';
          if (confirmCtx.kind === 'cancel') return 'Cancel loan';
          if (confirmCtx.kind === 'revertToPending') return 'Revert to Pending';
          if (confirmCtx.kind === 'undoRepayment') return 'Undo last repayment';
          return 'Confirm action';
        })()}
        message={(() => {
          if (!confirmCtx) return '';
          const code = confirmCtx.loan?.loan_code || confirmCtx.loan?.loanCode;
          const codeTxt = code ? `Loan #${code}` : `Loan #${confirmCtx.loan?.id}`;
          if (confirmCtx.kind === 'writeOff') return `Write off ${codeTxt}? This will set status to "Written off".`;
          if (confirmCtx.kind === 'undoWriteOff') return `Undo write-off for ${codeTxt}? This will restore status to "Outstanding".`;
          if (confirmCtx.kind === 'terminate') return `Terminate contract for ${codeTxt}?`;
          if (confirmCtx.kind === 'cancel') return `Cancel ${codeTxt}?`;
          if (confirmCtx.kind === 'revertToPending') return `Revert ${codeTxt} to Pending?`;
          if (confirmCtx.kind === 'undoRepayment') {
            const a = confirmCtx.extra?.amount;
            const d = confirmCtx.extra?.displayDate;
            const amtTxt = Number.isFinite(a) ? toCurrency(Number(a)) : '';
            const dateTxt = d ? ` dated ${d}` : '';
            return `Undo last repayment${amtTxt ? ` of ${amtTxt}` : ''}${dateTxt} for ${codeTxt}?`;
          }
          return `Confirm action for ${codeTxt}?`;
        })()}
        showNote={Boolean(confirmCtx && ['writeOff', 'undoWriteOff', 'terminate', 'cancel'].includes(confirmCtx.kind))}
        noteRequired={Boolean(confirmCtx && ['writeOff', 'undoWriteOff'].includes(confirmCtx.kind))}
        noteLabel={
          confirmCtx?.kind === 'cancel'
            ? 'Cancel reason (optional)'
            : confirmCtx?.kind === 'terminate'
              ? 'Termination note (optional)'
              : confirmCtx?.kind === 'undoWriteOff'
                ? 'Undo write-off note / reason (required)'
                : 'Write off note / reason (required)'
        }
        notePlaceholder={confirmCtx?.kind === 'writeOff' || confirmCtx?.kind === 'undoWriteOff' ? 'Enter reason…' : 'Optional note…'}
        confirmText={confirmCtx?.kind === 'writeOff' ? 'Yes, Write Off' : confirmCtx?.kind === 'undoWriteOff' ? 'Yes, Undo Write-Off' : confirmCtx?.kind === 'terminate' ? 'Yes, Terminate' : confirmCtx?.kind === 'cancel' ? 'Yes, Cancel' : confirmCtx?.kind === 'revertToPending' ? 'Yes, Revert' : confirmCtx?.kind === 'undoRepayment' ? 'Yes, Undo' : 'Confirm'}
        confirmClassName={confirmCtx?.kind === 'writeOff' ? 'bg-red-50 text-red-800 hover:bg-red-100' : confirmCtx?.kind === 'terminate' || confirmCtx?.kind === 'cancel' || confirmCtx?.kind === 'undoWriteOff' ? 'bg-amber-50 text-amber-800 hover:bg-amber-100' : 'bg-blue-50 text-blue-800 hover:bg-blue-100'}
        initialNote={confirmCtx?.extra?.note || ''}
        onClose={closeConfirm}
        onConfirm={async (note) => {
          if (!confirmCtx?.loan?.id) return;
          const loanId = confirmCtx.loan.id;
          if (confirmCtx.kind === 'writeOff') {
            await writeOffLoan(loanId, note);
            closeConfirm();
            setWriteOffRefresh((k) => k + 1);
            return;
          }
          if (confirmCtx.kind === 'undoWriteOff') {
            await undoWriteOffLoan(loanId, note);
            closeConfirm();
            return;
          }
          if (confirmCtx.kind === 'terminate') {
            await terminateContract(loanId, note);
            closeConfirm();
            return;
          }
          if (confirmCtx.kind === 'cancel') {
            await cancelLoan(loanId, note);
            closeConfirm();
            return;
          }
          if (confirmCtx.kind === 'revertToPending') {
            await revertLoanToPending(loanId);
            closeConfirm();
            return;
          }
          if (confirmCtx.kind === 'undoRepayment') {
            const repaymentId = confirmCtx.extra?.repaymentId;
            if (!repaymentId) throw new Error('No repayment found to undo.');
            await undoRepayment(loanId, repaymentId);
            closeConfirm();
            return;
          }
          closeConfirm();
        }}
      />

      {/* Repayment modal */}
      {showRepaymentForm && repaymentLoan && (
        <RepaymentForm
          loan={repaymentLoan}
          clientId={repaymentClientId}
          onClose={handleCloseRepaymentForm}
          onSuccess={async () => {
            setActionMessage('Repayment entered successfully');
            setActionType('success');
            handleCloseRepaymentForm();
            await refreshData();
          }}
        />
      )}

      {showLoanDetails && selectedLoan && (
          <LoanDetails
            loan={selectedLoan}
            onClose={() => {
              setShowLoanDetails(false);
              setSelectedLoan(null);
              setLoanDetailsSource('');
            }}
            canApprove={staffCan(selectedLoan, 'approve')}
            canReject={staffCan(selectedLoan, 'reject')}
            canRevertToPending={false}
            approveLoan={async (loanId, body) => {
              await approveLoan(loanId, body);
            }}
            onEdited={() => { setShowLoanDetails(false); setSelectedLoan(null); setLoanDetailsSource(''); refreshData(); }}
            onApproved={() => {
              // Close modal and navigate to dashboard after approval
              setShowLoanDetails(false);
              setSelectedLoan(null);
              setLoanDetailsSource('');
              navigate('/admin');
            }}
            handleShowRejectionForm={() => {}}
            moreDetailsNotes={moreDetailsNotes}
            setMoreDetailsNotes={setMoreDetailsNotes}
            showNoteInput={showNoteInput}
            setShowNoteInput={setShowNoteInput}
            userRole={'Admin'}
            onSavedNote={async () => {
              setAlert({ type: 'success', message: 'Note saved and sent to client' });
              await refreshData();
            }}
            staffSigning={
              (selectedLoan.status === 'Awaiting Staff' || selectedLoan.status === 'Awaiting Staff Signature')
                && loanDetailsSource !== 'overdue' ? (
                <StaffSigningContract
                  item={selectedLoan}
                  clients={clients}
                  onGenerate={handleGenerate}
                  isLoading={loading}
                  onEdit={() => {
                    // Open the shared LoanEditForm in staff-review mode
                    setEditLoan({ ...selectedLoan, clientName: selectedLoan.clientName || '' });
                    setEditMode('staff-review');
                    setShowEditForm(true);
                    setShowLoanDetails(false);
                    setSelectedLoan(null);
                    setLoanDetailsSource('');
                  }}
                />
              ) : null
            }
          />
      )}
      {showEditForm && editLoan && (
        <SharedLoanEditForm
          loan={editLoan}
          mode={editMode}
          onClose={() => {
            setShowEditForm(false);
            setEditLoan(null);
            setEditMode('');
            // refresh data after edit
            fetchLoans();
            fetchAwaitingQueue();
          }}
          onSaved={() => {
            setShowEditForm(false);
            setEditLoan(null);
            setEditMode('');
            fetchLoans();
            fetchAwaitingQueue();
          }}
        />
      )}

      {/* Reject dialog */}
      {rejectDialog.open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl overflow-hidden w-full max-w-md dark:bg-slate-800"
            style={{ boxShadow: '0 20px 40px -8px rgba(0,0,0,0.18), 0 4px 16px -4px rgba(239,68,68,0.12)' }}
          >
            <div className="h-1" style={{ background: 'linear-gradient(90deg, #ef4444, #f97316, #dc2626)' }} />
            <div className="px-5 py-3.5 border-b border-red-100/60 dark:border-red-900/60 flex items-center justify-between bg-gradient-to-r from-red-50 to-orange-50/20 dark:from-slate-800 dark:to-slate-800/80">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <span className="w-1 h-4 rounded-full bg-gradient-to-b from-red-500 to-orange-500 inline-block flex-shrink-0" />
                Reject Loan #{rejectDialog.loanId}
              </h3>
              <Button
                variant="text"
                size="sm"
                className="!p-1.5 rounded-full hover:bg-red-100/60 hover:text-red-600 dark:hover:bg-slate-700 dark:hover:text-slate-200 transition-colors"
                onClick={() => setRejectDialog({ open: false, loanId: null, reason: '' })}
                icon={
                  <svg className="w-4 h-4 text-gray-400 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                }
              />
            </div>
            <div className="p-5">
              <label className="block text-sm font-medium text-indigo-700 mb-1.5">Reason for Rejection</label>
              <textarea
                className="w-full focus:ring-red-500/20 focus:border-red-400 h-28 resize-none"
                value={rejectDialog.reason}
                onChange={(e) => setRejectDialog(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Provide a clear reason for rejection"
              />
            </div>
            <div className="px-5 py-3 border-t border-red-50 dark:border-slate-700 flex justify-end gap-2 bg-gradient-to-r from-red-50 to-orange-50/20 dark:from-slate-800 dark:to-slate-800/80">
              <Button variant="secondary" size="sm" onClick={() => setRejectDialog({ open: false, loanId: null, reason: '' })}>Cancel</Button>
              <Button
                variant="danger"
                size="sm"
                busyLabel="Rejecting…"
                onClick={async () => {
                  const reason = (rejectDialog.reason || '').trim();
                  if (!reason) {
                    setActionMessage('Rejection reason is required');
                    setActionType('error');
                    return;
                  }
                  await rejectLoan(rejectDialog.loanId, reason);
                  setRejectDialog({ open: false, loanId: null, reason: '' });
                }}
              >
                Confirm Reject
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Payments Upload Modal */}
      {showBulkUpload && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl overflow-hidden w-full max-w-xl dark:bg-slate-800"
            style={{ boxShadow: '0 20px 40px -8px rgba(0,0,0,0.18), 0 4px 16px -4px rgba(79,70,229,0.12)' }}
          >
            <div className="h-1" style={{ background: 'linear-gradient(90deg, #3b82f6, #6366f1, #8b5cf6)' }} />
            <div className="px-5 py-3.5 border-b border-indigo-100/60 dark:border-indigo-900/60 flex items-center justify-between bg-gradient-to-r from-gray-50 to-indigo-50/20 dark:from-slate-800 dark:to-slate-800/80">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <span className="w-1 h-4 rounded-full bg-gradient-to-b from-blue-500 to-indigo-500 inline-block flex-shrink-0" />
                Bulk Payment Upload
              </h3>
              <Button
                variant="text"
                size="sm"
                className="!p-1.5 rounded-full hover:bg-indigo-100/60 hover:text-indigo-600 dark:hover:bg-slate-700 dark:hover:text-slate-200 transition-colors"
                onClick={() => setShowBulkUpload(false)}
                title="Close"
                icon={
                  <svg className="w-4 h-4 text-gray-400 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                }
              />
            </div>
            <div className="p-5">
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-3">Upload a CSV file containing repayments. Use the sample format for columns and values.</p>
            <div className="text-xs text-gray-500 dark:text-slate-400 mb-3">
              Columns: <code className="bg-gray-50 dark:bg-slate-700 dark:text-slate-300 px-1 py-0.5 rounded">loan_id, client_id, amount_paid, payment_date, payment_method, payment_reference</code><br/>
              <span className="font-semibold text-gray-700 dark:text-slate-200">payment_reference is required on every row</span> (EFT reference, slip number or transaction ID); a row without one fails with "payment_reference required".<br/>
              payment_date format: YYYY-MM-DD. Methods: Cash, EFT/Bank Transfer, Debit Order, Card (POS/Speedpoint), PayToday, PayPulse, PayPulse (Bank Windhoek), BlueWallet, FNB eWallet/Pay2Cell, ATM Cash Deposit, Stop Order, MTC Pay, Other.
            </div>
            <div className="mb-3">
              <button
                type="button"
                onClick={downloadSampleCsv}
                className="text-blue-600 hover:underline text-sm"
                title="Download sample CSV"
              >
                Download sample CSV
              </button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const fileInput = e.currentTarget.querySelector('#bulk-csv-file');
                const file = fileInput?.files?.[0];
                if (!file) {
                  setActionMessage('Please choose a CSV file to upload');
                  setActionType('warning');
                  return;
                }
                try {
                  setBulkUploading(true);
                  const fd = new FormData();
                  fd.append('file', file);
                  const res = await axios.post(
                    apiUrl('/api/loans/bulk-repayments'),
                    fd,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' } }
                  );
                  setBulkResult(res.data);
                  setActionMessage('Bulk upload completed');
                  setActionType('success');
                  // Auto-download disabled; users can click buttons to download CSVs
                  await refreshData();
                } catch (err) {
                  const msg = err?.response?.data?.error || 'Bulk upload failed';
                  setActionMessage(msg);
                  setActionType('error');
                } finally {
                  setBulkUploading(false);
                }
              }}
            >
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">CSV File</label>
                <div
                  className="border-2 border-dashed rounded p-4 text-center cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 dark:border-slate-600"
                  onClick={() => document.getElementById('bulk-csv-file')?.click()}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const input = document.getElementById('bulk-csv-file');
                    if (input && e.dataTransfer?.files?.length) {
                      input.files = e.dataTransfer.files;
                      // trigger change event so UI updates
                      const ev = new Event('change', { bubbles: true });
                      input.dispatchEvent(ev);
                    }
                  }}
                >
                  <div className="text-gray-600 dark:text-slate-400 text-sm">Drag & drop CSV here, or click to browse</div>
                  <div className="mt-2">
                    <input id="bulk-csv-file" type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => {
                      const name = e.target.files?.[0]?.name || '';
                      const label = document.getElementById('bulk-csv-file-label');
                      if (label) label.textContent = name ? `Selected: ${name}` : 'No file selected';
                    }} />
                    <div id="bulk-csv-file-label" className="text-xs text-gray-500 dark:text-slate-400">No file selected</div>
                  </div>
                </div>
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    className="text-xs text-gray-600 dark:text-slate-400 hover:underline"
                    onClick={() => {
                      const input = document.getElementById('bulk-csv-file');
                      if (input) {
                        input.value = '';
                        const label = document.getElementById('bulk-csv-file-label');
                        if (label) label.textContent = 'No file selected';
                      }
                    }}
                  >
                    Clear selection
                  </button>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={() => setShowBulkUpload(false)}>Cancel</Button>
                <Button variant="primary" size="sm" type="submit" disabled={bulkUploading} loading={bulkUploading} busyLabel="Uploading…"
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                    </svg>
                  }
                >
                  {bulkUploading ? 'Uploading…' : 'Upload CSV'}
                </Button>
              </div>
            </form>
            {bulkResult && (
              <div className="mt-4 border-t dark:border-slate-700 pt-3">
                <div className="text-sm text-gray-700 dark:text-slate-300 mb-2">Processed: {bulkResult.processed} • Success: {bulkResult.success} • Failed: {bulkResult.failed}</div>
                {Array.isArray(bulkResult.results) && bulkResult.results.length > 0 && (
                  <div className="max-h-40 overflow-auto border rounded p-2 dark:border-slate-600">
                    <ResponsiveTable
                      data={bulkResult.results}
                      columns={[
                        { field: 'row', header: 'Row' },
                        { field: 'loanId', header: 'Loan', formatter: (value) => (value ? <LoanLink loanId={value}>{value}</LoanLink> : '-') },
                        { field: 'status', header: 'Status' },
                        { field: 'message', header: 'Message', formatter: (value) => value || '-' },
                      ]}
                    />
                  </div>
                )}
                <div className="mt-3 flex gap-2">
                  {bulkResult.failedCsv && (
                    <button
                      type="button"
                      className="px-3 py-1 rounded bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 text-sm"
                      onClick={() => {
                        const blob = new Blob([bulkResult.failedCsv], { type: 'text/csv;charset=utf-8;' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'bulk-repayments-failed.csv';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }}
                    >
                      Download Failed CSV
                    </button>
                  )}
                  {bulkResult.successCsv && (
                    <button
                      type="button"
                      className="px-3 py-1 rounded bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 text-sm"
                      onClick={() => {
                        const blob = new Blob([bulkResult.successCsv], { type: 'text/csv;charset=utf-8;' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'bulk-repayments-success.csv';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }}
                    >
                      Download Success CSV
                    </button>
                  )}
                </div>
              </div>
            )}
            </div>{/* end p-5 */}
          </div>
        </div>
      )}

      {showInvoiceModal && invoiceLoan && (
        <InvoiceModal
          visible={showInvoiceModal}
          loan={invoiceLoan}
          onClose={() => { setShowInvoiceModal(false); setInvoiceLoan(null); }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;